import type {
  ConfirmErrors,
  SendCodeErrors,
} from './types';

import { ResponseStatus } from '../../../constants';
import type { ServiceError } from '../../../utils/create-http-api-service';
import { createError, DEFAULT_ERROR_MESSAGE } from '../../../utils/default-error-adapter';
import {
  CodeError,
  PhoneError,
} from './constants';

export type InputFieldErrorKey = 'Token' | 'NewPhone' | 'Email';

const isInputFieldError = <T extends InputFieldErrorKey>(
  error: object,
  key: T,
): error is { errors: Record<T, [string]> } => {
  if (!('errors' in error)) {
    return false;
  }
  const errorWithType = error as { errors: unknown };
  if (!(errorWithType.errors instanceof Object)) {
    return false;
  }
  const errors = errorWithType.errors as Record<string, unknown>;
  return key in errors && Array.isArray(errors[key]);
};

export type ErrorPropertyKey = 'detail' | 'type';

const hasErrorPropertyByKey = <T extends ErrorPropertyKey>(
  error: object,
  key: T,
): error is Record<T, string> => {
  if (!(key in error)) {
    return false;
  }
  const errorWithDetail = error as Record<T, unknown>;
  return typeof errorWithDetail[key] === 'string';
};

export const changePhoneNumberErrorHandler = (error: ServiceError) => {
  const response = error.response;
  const errors: SendCodeErrors = {};
  if (!response) {
    return createError(null);
  }

  const { status, statusText, data } = response;
  if (!(data instanceof Object)) {
    return createError(null);
  }

  if (status === ResponseStatus.UNPROCESSABLE_ENTITY && isInputFieldError(data, 'NewPhone')) {
    errors.phone = data.errors.NewPhone[0] && PhoneError.INCORRECT_PHONE;

    return createError(errors, status, statusText);
  }

  if (status === ResponseStatus.TOO_MANY_REQUESTS) {
    if (hasErrorPropertyByKey(data, 'type') && data.type.endsWith('request-limited')) {
      errors.nextStep = true;
      errors.codeData = { ...data, token: '' };
    }
  }
  else if (status === ResponseStatus.UNPROCESSABLE_ENTITY) {
    if (!hasErrorPropertyByKey(data, 'type')) {
      errors.alert = DEFAULT_ERROR_MESSAGE;

      return createError(errors, status, statusText);
    }
    if (data.type.endsWith('not-ready-to-resend')) {
      errors.nextStep = true;
      errors.codeData = data;
    }
    if (data.type.endsWith('invalid-password')) {
      errors.password = PhoneError.INCORRECT_PASSWORD;
    }
  }
  else {
    errors.alert = statusText;
    if (hasErrorPropertyByKey(data, 'detail')) {
      errors.alert = data.detail;
    }
  }

  return createError(errors, status, statusText);
};

export const changePhoneNumberConfirmErrorHandler = (error: ServiceError) => {
  const response = error.response;
  const errors: ConfirmErrors = {};
  if (!response) {
    return createError(null);
  }

  const { status, statusText, data } = response;
  if (!(data instanceof Object)) {
    return createError(null);
  }

  if (status === ResponseStatus.UNPROCESSABLE_ENTITY && isInputFieldError(data, 'Token')) {
    errors.code = data.errors.Token[0] && CodeError.NOT_TOKEN;

    return createError(errors, status, statusText);
  }

  if (status === ResponseStatus.IS_REGISTERED) {
    if (!hasErrorPropertyByKey(data, 'type') || !hasErrorPropertyByKey(data, 'detail')) {
      return createError(errors, status, statusText);
    }
    if (data.type.endsWith('phone-number-already-registered')) {
      errors.isRegistered = data.detail;
    }
  }
  else if (status === ResponseStatus.UNPROCESSABLE_ENTITY) {
    if (!hasErrorPropertyByKey(data, 'type')) {
      errors.alert = DEFAULT_ERROR_MESSAGE;

      return createError(errors, status, statusText);
    }
    if (data.type.endsWith('expired-token')) {
      errors.code = CodeError.NOT_TOKEN;
    }
    if (data.type.endsWith('secure-code-token-not-found')) {
      errors.code = CodeError.NOT_TOKEN;
    }
    if (data.type.endsWith('wrong-code')) {
      errors.code = CodeError.NOT_CODE;
    }
  }
  else {
    errors.code = CodeError.SOMETHING_WRONG;
    errors.alert = statusText;

    if (hasErrorPropertyByKey(data, 'detail')) {
      errors.alert = data.detail;
    }
  }

  return createError(errors, status, statusText);
};

export const sendCodeErrorHandler = (error: ServiceError) => {
  const response = error.response;
  const errors: SendCodeErrors = {};
  if (!response) {
    return createError(null);
  }

  const { status, statusText, data } = response;
  if (!(data instanceof Object)) {
    return createError(null);
  }

  if (status === ResponseStatus.UNPROCESSABLE_ENTITY && isInputFieldError(data, 'Email')) {
    errors.emailNotFake = data.errors.Email[0];

    return createError(errors, status, statusText);
  }

  if (status === ResponseStatus.IS_REGISTERED) {
    if (hasErrorPropertyByKey(data, 'type') && data.type.endsWith('email-already-registered')) {
      errors.isRegistered = 'Пользователь с такой почтой уже зарегистрирован';
    }

    return createError(errors, status, statusText);
  }
  else if (status === ResponseStatus.TOO_MANY_REQUESTS) {
    if (hasErrorPropertyByKey(data, 'type') && data.type.endsWith('request-limited')) {
      errors.nextStep = true;
      errors.codeData = { ...data, token: '' };
    }
  }
  else if (status === ResponseStatus.UNPROCESSABLE_ENTITY) {
    if (!hasErrorPropertyByKey(data, 'type')) {
      errors.alert = DEFAULT_ERROR_MESSAGE;

      return createError(errors, status, statusText);
    }
    if (data.type.endsWith('not-ready-to-resend')) {
      errors.nextStep = true;
      errors.codeData = { ...data, token: '' };
    }
    if (data.type.endsWith('invalid-password')) {
      // TODO Заменить хардкод на данные с бека, когда они будут корректные
      // errors.password = payload.detail;
      errors.password = 'Неверный пароль';
    }
    else {
      // TODO Для текущей задачи неактуально,
      // но когда сделают описание ошибок с бека, нужно заменить дефолтное сообщение, на сообщение с бека
      errors.alert = DEFAULT_ERROR_MESSAGE;
    }
  }
  else {
    errors.alert = statusText;
    if (hasErrorPropertyByKey(data, 'detail')) {
      errors.alert = data.detail;
    }
  }

  return createError(errors, status, statusText);
};

export const emailConfirmErrorHandler = (error: ServiceError) => {
  const response = error.response;
  const errors: ConfirmErrors = {};
  if (!response) {
    return createError(null);
  }

  const { status, statusText, data } = response;
  if (!(data instanceof Object)) {
    return createError(null);
  }

  // TODO сделано по аналогии с RestorePassword, но потом желательно переписать, потому что с бека приходят нормальные данные
  if (status === ResponseStatus.UNPROCESSABLE_ENTITY) {
    if (!hasErrorPropertyByKey(data, 'type')) {
      errors.alert = DEFAULT_ERROR_MESSAGE;

      return createError(errors, status, statusText);
    }
    errors.code = data.type.endsWith('secure-code-token-not-found')
      ? CodeError.NOT_TOKEN
      : CodeError.NOT_CODE;
  }
  else {
    errors.code = CodeError.SOMETHING_WRONG;
    errors.alert = statusText;

    if (hasErrorPropertyByKey(data, 'detail')) {
      errors.alert = data.detail;
    }
  }

  return createError(errors, status, statusText);
};
